import { useCallback, useEffect, useRef } from 'react';
import { create } from 'zustand';
import { toast } from 'sonner';
import type { DateRange } from 'react-day-picker';

import { fetchMediaStatsBatched } from '../api/statistics';

import type { BatchProgress, MediaStats, MediaStatsError } from '../types/statistics';

type BatchedStatsState = {
  /**
   * Что вставлено в поле ссылок, период и разбивка на страницы отчёта живут
   * рядом с результатом: иначе после возврата на вкладку выгрузки «по
   * понедельникам», ежемесячная и ежедневная шли одной страницей, а Docs — без периода.
   */
  text: string;
  dateRange: DateRange | undefined;
  pages: string[][][];
  results: MediaStats[];
  errors: MediaStatsError[];
  originalUrls: string[];
  progress: BatchProgress | null;
  isPending: boolean;
  isPaused: boolean;
  pauseReason: string | null;
  elapsedSeconds: number;
  /** Когда начат сбор: счётчик времени не обнуляется при возврате на вкладку. */
  startedAt: number;
};

const INITIAL_STATE: BatchedStatsState = {
  text: '',
  dateRange: undefined,
  pages: [],
  results: [],
  errors: [],
  originalUrls: [],
  progress: null,
  isPending: false,
  isPaused: false,
  pauseReason: null,
  elapsedSeconds: 0,
  startedAt: 0,
};

/**
 * Состояние живёт в сторе, а не в компоненте: собрать метрики по сотне ссылок —
 * это минуты ожидания, и уход на соседнюю вкладку не должен их обнулять.
 * Результат ждёт человека на месте, пока он не запустит новый сбор.
 */
const useBatchedStatsStore = create<BatchedStatsState>(() => INITIAL_STATE);

const setState = useBatchedStatsStore.setState;

export const setLinksText = (text: string) => setState({ text });
export const setLinksDateRange = (dateRange: DateRange | undefined) => setState({ dateRange });
export const setLinksPages = (pages: string[][][]) => setState({ pages });

// Сбор переживает уход со вкладки — значит, и отменять его надо уметь после
// возврата: контроллер общий, а не в ref компонента.
let activeController: AbortController | null = null;

const ESTIMATED_SECONDS_PER_URL = 3;

function computeSmoothedPercent(
  realPercent: number,
  elapsedSeconds: number,
  totalUrls: number,
  completedUrls: number
): number {
  const batchSize = Math.min(50, totalUrls - completedUrls);
  const nextRealPercent = Math.min(
    Math.round(((completedUrls + batchSize) / totalUrls) * 100),
    100
  );

  const estimatedBatchSeconds = batchSize * ESTIMATED_SECONDS_PER_URL;
  const elapsedInBatch = elapsedSeconds - completedUrls * ESTIMATED_SECONDS_PER_URL;
  const batchRatio = Math.max(0, elapsedInBatch) / estimatedBatchSeconds;

  const range = nextRealPercent - realPercent;
  const smoothed = realPercent + range * 0.95 * (1 - Math.exp(-2 * batchRatio));

  return Math.min(Math.round(smoothed), 99);
}

export function useBatchedStats() {
  const state = useBatchedStatsStore();
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!state.isPending) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    const startedAt = useBatchedStatsStore.getState().startedAt || Date.now();
    timerRef.current = setInterval(() => {
      const seconds = Math.round((Date.now() - startedAt) / 1000);
      setState(prev => ({ ...prev, elapsedSeconds: seconds }));
    }, 500);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [state.isPending]);

  const startBatch = useCallback(
    async (urls: string[], dateFrom?: string, dateTo?: string) => {
      activeController?.abort();
      const controller = new AbortController();
      activeController = controller;

      setState({
        results: [],
        errors: [],
        originalUrls: urls,
        progress: {
          completedUrls: 0,
          totalUrls: urls.length,
          currentBatch: 1,
          totalBatches: Math.ceil(urls.length / 50),
          percent: 0,
        },
        isPending: true,
        isPaused: false,
        pauseReason: null,
        elapsedSeconds: 0,
        startedAt: Date.now(),
      });

      try {
        const { results, errors } = await fetchMediaStatsBatched(
          { urls, dateFrom, dateTo },
          {
            onProgress(progress) {
              setState(prev => ({ ...prev, progress }));
            },
            onBatchResults(batchResults, batchErrors) {
              setState(prev => ({
                ...prev,
                results: [...prev.results, ...batchResults],
                errors: [...prev.errors, ...batchErrors],
              }));
            },
            onPause(reason) {
              setState(prev => ({
                ...prev,
                isPaused: true,
                pauseReason: reason,
              }));
            },
            onResume() {
              setState(prev => ({
                ...prev,
                isPaused: false,
                pauseReason: null,
              }));
            },
          },
          controller.signal
        );

        setState(prev => ({
          ...prev,
          results,
          errors,
          isPending: false,
          isPaused: false,
          pauseReason: null,
          progress: prev.progress
            ? { ...prev.progress, percent: 100, completedUrls: prev.progress.totalUrls }
            : null,
        }));

        if (results.length === 0 && errors.length > 0) {
          toast.warning('Не удалось получить статистику ни по одной ссылке');
        } else if (results.length > 0 && errors.length > 0) {
          toast.warning(
            `Статистика получена для ${results.length} из ${results.length + errors.length} ссылок`
          );
        } else if (results.length > 0 && errors.length === 0) {
          toast.success(`Статистика получена для ${results.length} ссылок`);
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          setState(prev => ({
            ...prev,
            isPending: false,
            isPaused: false,
            pauseReason: null,
          }));
          return;
        }

        toast.error(
          `Ошибка получения статистики: ${err instanceof Error ? err.message : 'Неизвестная ошибка'}`
        );
        setState(prev => ({
          ...prev,
          isPending: false,
          isPaused: false,
          pauseReason: null,
        }));
      }
    },
    []
  );

  const cancel = useCallback(() => {
    activeController?.abort();
  }, []);

  const reset = useCallback(() => {
    activeController?.abort();
    setState(INITIAL_STATE);
  }, []);

  const displayPercent =
    state.isPending && state.progress
      ? computeSmoothedPercent(
          state.progress.percent,
          state.elapsedSeconds,
          state.progress.totalUrls,
          state.progress.completedUrls
        )
      : (state.progress?.percent ?? 0);

  return {
    ...state,
    displayPercent,
    startBatch,
    cancel,
    reset,
  };
}
